import { LocaleProvider, useLocaleState } from '@alice-financial/alice-sdk'
import * as React from 'react'
import { IntlProvider as ReactIntlProvider } from 'react-intl'
import { LocalizationProviderProps } from './types'

/**
 * A wrapper around React-Intl IntlProvider that will change the translation
 * settings based on the currently-loaded user object from the API. In order
 * to change the language, you must update the user preference
 */
const TranslationProvider = ({
  children,
  messageMap,
  defaultRichTextElements,
  ...intlConfig
}: LocalizationProviderProps) => {
  const [locale] = useLocaleState()
  const messages = messageMap[locale]
  return (
    <ReactIntlProvider
      locale={locale}
      defaultLocale="en-us"
      messages={messages}
      defaultRichTextElements={{
        strong: (...chunks) => <strong>{chunks}</strong>,
        'strong-nowrap': (...chunks) => <strong style={{ whiteSpace: 'nowrap' }}>{chunks}</strong>,
        br: () => <br />,
        em: (...chunks) => <em>{chunks}</em>,
        li: (...chunks) => <li>{chunks}</li>,
        ...defaultRichTextElements,
      }}
      {...intlConfig}
    >
      {children}
    </ReactIntlProvider>
  )
}

/**
 * This provider should wrap the application in order to provide the correct React-Intl
 * transalations as well as enabling the `useLocaleState` hook, which any child component
 * can use to get/set the user's preferred locale
 */
export const LocalizationProvider = ({ children, messageMap, ...intlConfig }: LocalizationProviderProps) => (
  <LocaleProvider>
    <TranslationProvider messageMap={messageMap} {...intlConfig}>
      {children}
    </TranslationProvider>
  </LocaleProvider>
)
